// cSpell:ignore Betaalstatus betaaluitnodiging Betalingen dtos forfait geen Gelijke groepen groepskost kosten nummer onderdeel pand panden stad Straat unthrottled verdeling verzonden
/* eslint-disable no-nested-ternary */
// Thin hooks over the mock API. Mirrors the shape of @rentiohq V2 hooks
// (useDetail/usePaged/useUpdate) so swapping to a real backend later is mechanical.

import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as contactApi from "@rentiohq/shared-frontend/dist/redux/contact/contact.api";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as apportionmentKeyHooks from "@rentiohq/shared-frontend/dist/reduxV2/apportionmentKey/apportionmentKey.hooks";
import * as apportionmentKeyPromises from "@rentiohq/shared-frontend/dist/reduxV2/apportionmentKey/apportionmentKey.promises";
import { IApportionmentKey } from "@rentiohq/shared-frontend/dist/reduxV2/apportionmentKey/apportionmentKey.types";
import * as contractHooksV2 from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.hooks";
import { generateActions } from "@rentiohq/shared-frontend/dist/reduxV2/generators/generator.actions";
import * as paymentOrderPromises from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.promises";
import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import * as propertyApi from "@rentiohq/shared-frontend/dist/reduxV2/property/property.api";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { GENERATOR_PARAMS as PROPERTY_COST_GENERATOR_PARAMS } from "@rentiohq/shared-frontend/dist/reduxV2/propertyCost/propertyCost.constants";
import * as propertyCostHooks from "@rentiohq/shared-frontend/dist/reduxV2/propertyCost/propertyCost.hooks";
import * as propertyCostPromises from "@rentiohq/shared-frontend/dist/reduxV2/propertyCost/propertyCost.promises";
import { IPropertyCost } from "@rentiohq/shared-frontend/dist/reduxV2/propertyCost/propertyCost.types";
import * as settlementHooks from "@rentiohq/shared-frontend/dist/reduxV2/settlement/settlement.hooks";
import * as settlementPromises from "@rentiohq/shared-frontend/dist/reduxV2/settlement/settlement.promises";
import { ISettlement } from "@rentiohq/shared-frontend/dist/reduxV2/settlement/settlement.types";
import {
  IGetAllQueryAsAction,
  TSort,
} from "@rentiohq/shared-frontend/dist/reduxV2/utils/api.types";
import {
  EContractMemberTypes,
  EContractType,
} from "@rentiohq/shared-frontend/dist/types/contract.types";
import { EPayoutType } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { EPaymentRequestStatus } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import {
  EPropertyMemberTypes,
  EPropertyTypeId,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { uniq } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { unwrapEntity } from "./apiResponse.utils";
import {
  keyFromDto,
  keyPatchToUpdateDto,
  keyToCreateDto,
} from "./apportionmentKey.mapper";
import {
  contractProvision,
  leaseFixedFeeFlags,
} from "./contractProvision.utils";
import {
  ICostsEditPermission,
  needsCostsEditCheck,
  resolveCostsEditPermission,
} from "./costsEditAccess.utils";
import { hasLoadFailed, uniqueById } from "./costsList.utils";
import {
  addressPartsOf,
  unitDisplayLabel,
  unitScreenLabel,
} from "./groupUnits.utils";
import { leaseChargesVat } from "./leaseVat.utils";
import { todayIsoDay, toBrusselsIsoDay } from "./localDay.utils";
import { getDefaultPayerId, getPayerCandidates } from "./payer.utils";
import {
  invoicePatchToUpdateDto,
  invoiceToCreateDto,
  propertyCostToInvoice,
} from "./propertyCost.mapper";
import { ProvisionSchedule } from "./provisionHistory";
import {
  settlementFromApi,
  settlementPatchToUpdateDto,
  settlementToCreateDto,
} from "./settlement.mapper";
import {
  settlementPayStatusOf,
  TSettlementPayStatus,
} from "./settlementListColumns.utils";
import {
  getActionPaymentStatus,
  getLiveSettlementActions,
  getSettlementActionPaidAt,
  getSettlementRefundDoneAt,
  isSettlementActionPaid,
  isSettlementRefundDone,
  settlementOpenPayments,
} from "./settlementPaymentStatus.utils";
import { contractParties, joinPartyNames } from "./settlementView.utils";
import { IPostalAddress, postalAddressOf } from "./statementParties.utils";
import {
  ApportionmentKey,
  ContractRef,
  Invoice,
  PaymentSplit,
  Settlement,
  SettlementAction,
  SettlementSnapshot,
  isTenantPaidStatus,
} from "./types";
import { useEndedPaymentOrderIds } from "./useEndedPaymentOrderIds";
import { useRentPayers } from "./useRentPayers";

// Kept available here for the screens that delete costs and settlements.
export { deleteRefusedMessage } from "./costsList.utils";

// A real rental contract of a property, reduced to what the cost-distribution
// and per-tenant payment need.
export interface RentalContract {
  ref: ContractRef;
  tenantAccountId?: number;
  tenantName: string;
}

// Contract dates are calendar days: read them as the Brussels day, the same
// way the backend does. toISOString() gives the UTC day, which is the day
// before for a date stored as Brussels midnight (22:00/23:00 UTC).
const toIsoDate = (d: any) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Brussels" }).format(
    d instanceof Date ? d : new Date(d),
  );

// Reduce a raw contract to the cost-distribution/payment shape. `unitId` is the
// property the contract belongs to (a standalone property, or one unit of a building).
function mapContractToRental(params: {
  c: any;
  unitId: string;
  rentPayerId?: number;
}): RentalContract {
  const { c, unitId, rentPayerId } = params;
  // Every tenant is party to the settlement, so the lease is named after all
  // of them. The payer is whoever pays the lease's rent, else its only tenant
  // or parent; otherwise the broker chooses among them, and until then the
  // first tenant (a parent only when the lease has no tenant) stands in,
  // whatever order the API lists the members in.
  const nameOf = (m: any) => (m.account ? getName(m.account) : "");
  const { tenantNames, parentNames, payerAccountId } = contractParties({
    members: c.members ?? [],
    nameOf,
    tenantRole: EContractMemberTypes.Tenant,
    parentRole: EContractMemberTypes.Parent,
  });
  const payerCandidates = getPayerCandidates({
    members: c.members ?? [],
    nameOf,
    rentPayerId,
  });
  const defaultPayerId = getDefaultPayerId(payerCandidates);
  const tenantName =
    joinPartyNames(tenantNames) ||
    joinPartyNames(parentNames) ||
    getLocalizedText("costs.field.tenant");

  // Monthly provision per slice, incl. the VAT the lease charges on each line
  // (see contractProvision.utils).
  const {
    monthlyProvision,
    monthlyProvisionCommon,
    monthlyProvisionPrivate,
    provisionUnchanged,
    provisionIndexed,
    provisionVatIncluded,
    provisionVatFactor,
    provisionLines,
  } = contractProvision(c);
  // The kinds the lease prices as a forfait, not settled with this tenant.
  const { fixedFeeCommon, fixedFeePrivate } = leaseFixedFeeFlags(c);

  return {
    ref: {
      id: String(c.id),
      unitId,
      unitCode: tenantName,
      tenantName,
      tenantAccountId: defaultPayerId ?? payerAccountId,
      payerCandidates,
      isPayerResolved: defaultPayerId != null,
      tenantNames,
      parentNames,
      startDate: toIsoDate(c.startDate),
      endDate: c.stopDate ? toIsoDate(c.stopDate) : "2999-12-31",
      monthlyProvision,
      monthlyProvisionCommon,
      monthlyProvisionPrivate,
      fixedFeeCommon,
      fixedFeePrivate,
      provisionUnchanged,
      provisionIndexed,
      provisionVatIncluded,
      provisionVatFactor,
      provisionLines,
      leaseChargesVat: leaseChargesVat(c),
    },
    tenantAccountId: defaultPayerId ?? payerAccountId,
    tenantName,
  };
}

// Provision history of the given contracts over from..to (requested amounts
// and dated indexations), for the expected-provision step. `null` while it
// loads; an empty map when the request fails — the step then falls back to
// the contracts' current amounts, flagged as an estimate.
export function useProvisionSchedules(params: {
  propertyId?: number;
  contractIds: string[];
  from?: string;
  to?: string;
}): Record<string, ProvisionSchedule> | null {
  const { propertyId, contractIds, from, to } = params;
  const [schedules, setSchedules] = useState<Record<
    string,
    ProvisionSchedule
  > | null>(null);
  const idsKey = uniq(contractIds).sort().join(",");

  useEffect(() => {
    if (!propertyId || !from || !to || idsKey === "") {
      setSchedules({});
      return;
    }
    let cancelled = false;
    setSchedules(null);
    const query = new URLSearchParams({
      propertyId: String(propertyId),
      contractIds: idsKey,
      from,
      to,
    });
    api
      .get<{ data: Record<string, ProvisionSchedule> }>(
        `/settlements/provision-schedule?${query.toString()}`,
        { isV2: true },
      )
      .then(response => {
        if (!cancelled) setSchedules(response.data?.data ?? {});
      })
      .catch(() => {
        if (!cancelled) setSchedules({});
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId, idsKey, from, to]);

  return schedules;
}

// What a statement needs of an account's contact: its language (nl_BE /
// fr_FR / en_UK), its company name and its postal address.
export interface IAccountContact {
  locale?: string;
  company?: string;
  address?: IPostalAddress;
}

// The contact of each given account, so a settlement statement is rendered
// in its recipient's language and addressed to them, and names the office
// that sends it. Accounts without a contact are simply absent: callers fall
// back to the broker's own language and to the leased unit's address. One
// request for all ids.
export function useAccountContacts(
  accountIds: number[],
): Record<number, IAccountContact> {
  const [contacts, setContacts] = useState<Record<number, IAccountContact>>({});
  const idsKey = uniq(accountIds.filter(id => id > 0))
    .sort((x, y) => x - y)
    .join(",");

  useEffect(() => {
    let cancelled = false;
    const ids = idsKey === "" ? [] : idsKey.split(",").map(Number);
    if (ids.length === 0) {
      setContacts({});
      return;
    }
    contactApi
      .getContactsByAccountIds(ids)
      .then(response => {
        const out: Record<number, IAccountContact> = {};
        for (const contact of (response.data as any[]) ?? []) {
          if (!contact) continue;
          const entry: IAccountContact = {
            ...(contact.locale ? { locale: contact.locale } : {}),
            ...(contact.company?.trim()
              ? { company: contact.company.trim() }
              : {}),
            ...(postalAddressOf(contact)
              ? { address: postalAddressOf(contact) }
              : {}),
          };
          for (const accountId of contact.accountIds ?? []) {
            if (ids.includes(accountId)) out[accountId] = entry;
          }
        }
        if (!cancelled) setContacts(out);
      })
      .catch(() => {
        if (!cancelled) setContacts({});
      });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return contacts;
}

// Owners of each given property (pand). The owner is the property's, not the
// contract's (Lewis, 2026-10-05). Only the property detail carries members,
// hence one fetch per property.
// - keyByProperty: the sorted owner account ids joined — equal keys mean the
//   same owner(s); "" when a property lists no owner.
// - nameByKey: the owners' names for display, per key.
// - loaded: every property answered (a failed fetch counts as answered with
//   an unknown owner, so callers can fail closed).
export function usePropertyOwners(propertyIds: number[]): {
  keyByProperty: Record<number, string>;
  nameByKey: Record<string, string>;
  loaded: boolean;
} {
  const [state, setState] = useState<{
    idsKey: string;
    keyByProperty: Record<number, string>;
    nameByKey: Record<string, string>;
  }>({ idsKey: "", keyByProperty: {}, nameByKey: {} });
  const idsKey = uniq(propertyIds.filter(id => id > 0))
    .sort((x, y) => x - y)
    .join(",");

  useEffect(() => {
    let cancelled = false;
    const ids = idsKey === "" ? [] : idsKey.split(",").map(Number);
    Promise.all(
      ids.map(async id => {
        try {
          const response = await propertyApi.getDetail({ id });
          const owners = (response.data.data?.members ?? [])
            .filter(m => (m.roles ?? []).includes(EPropertyMemberTypes.Owner))
            .map(m => m.account)
            .filter(account => account?.id != null)
            .sort((x, y) => x.id - y.id);
          const key = owners.map(o => o.id).join(",");
          const name = owners
            .map(o =>
              o.company?.trim()
                ? o.company.trim()
                : [o.firstname, o.lastname].filter(Boolean).join(" "),
            )
            .filter(Boolean)
            .join(" & ");
          return { id, key, name };
        } catch {
          return { id, key: "", name: "" };
        }
      }),
    ).then(entries => {
      if (cancelled) return;
      const keyByProperty: Record<number, string> = {};
      const nameByKey: Record<string, string> = {};
      for (const e of entries) {
        keyByProperty[e.id] = e.key;
        if (e.key && e.name) nameByKey[e.key] = e.name;
      }
      setState({ idsKey, keyByProperty, nameByKey });
    });
    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return {
    keyByProperty: state.keyByProperty,
    nameByKey: state.nameByKey,
    loaded: state.idsKey === idsKey,
  };
}

// The lists below page through every row with LIMIT/OFFSET. The contract
// list's default sort (createdAt, to the second) is not unique: contracts
// imported together share it, so two pages could overlap — a tenant was then
// listed twice (and billed twice) while another dropped out (and the owner
// paid their share as vacancy). The id as last sort key keeps pages apart.
const CONTRACTS_PAGING_SORT: TSort = [
  { field: "createdAt", method: "ASC" },
  { field: "id", method: "ASC" },
];

// Fetches the property's real rental contracts (active + ended), so the cost
// can be split over the tenants that actually occupied it during the period,
// and whether they have loaded: a list that is still empty because it is
// fetching is not a unit without leases.
export function usePropertyRentalContractsState(propertyId?: number): {
  rentals: RentalContract[];
  isLoaded: boolean;
} {
  const enabled = propertyId != null && !Number.isNaN(propertyId);
  const filterId = enabled ? (propertyId as number) : -1;

  // One filter over EVERY Basic contract on this property (active or ended,
  // stopDate null or not). The wizard's period-overlap check already decides
  // which contracts settle — splitting the fetch into Active/Ended dropped
  // previous tenants whose contract had no stopDate or fell between the two
  // filter clauses in the redux cache.
  const { items, isFetching } = contractHooksV2.useGetAll({
    shouldRefetch: enabled,
    query: {
      filter: {
        and: [
          { contractType: { eq: EContractType.Basic } },
          { propertyId: { eq: filterId } },
        ],
      },
      sort: CONTRACTS_PAGING_SORT,
    },
  });
  const rentPayerByContract = useRentPayers(enabled ? [filterId] : []);

  const rentals = useMemo(() => {
    if (!enabled) return [];
    return uniqueById(items).map((c: any) =>
      mapContractToRental({
        c,
        unitId: String(propertyId),
        rentPayerId: rentPayerByContract[String(c.id)],
      }),
    );
  }, [enabled, items, propertyId, rentPayerByContract]);

  return { rentals, isLoaded: enabled && items !== undefined && !isFetching };
}

// The same leases, without their loading state.
export function usePropertyRentalContracts(
  propertyId?: number,
): RentalContract[] {
  return usePropertyRentalContractsState(propertyId).rentals;
}

// A unit of a building, reduced to id + display label.
export interface BuildingUnit {
  id: number;
  // Short label for the screens, marked when the unit is archived.
  label: string;
  // Full address without that mark, for what is frozen or sent.
  address: string;
  // Calendar day the unit was archived; absent while it is active.
  archivedOn?: string;
}

// Fetches the real child properties (units) of a building/group, archived
// ones included: a unit archived since still belongs to the periods in which
// it existed. Callers that split money keep the units of their own period
// (see groupUnits.utils).
export function useGroupUnits(groupId?: string): {
  units: BuildingUnit[];
  isFetching: boolean;
} {
  const enabled = !!groupId && !Number.isNaN(Number(groupId));
  const parentId = enabled ? Number(groupId) : -1;

  const { items, isFetching } = propertyHooks.useGetAll({
    shouldRefetch: enabled,
    query: { filter: { parentId: { eq: parentId } } },
  });

  const units = useMemo(() => {
    if (!enabled) return [];
    return uniqueById(items).map((p: any) => {
      const shortLabel =
        formatAddress(p, false) ||
        p.name ||
        `${getLocalizedText("costs.field.property")} ${p.id}`;
      const archivedOn = p.archivedAt ? toIsoDate(p.archivedAt) : undefined;
      return {
        id: Number(p.id),
        label: unitDisplayLabel({
          label: unitScreenLabel({ property: p, fallback: shortLabel }),
          archivedOn,
        }),
        address: formatAddress(p) || shortLabel,
        archivedOn,
      };
    });
  }, [enabled, items]);

  return { units, isFetching };
}

// Fetches the real rental contracts (active + ended) of every unit of a building
// in one query, each tagged with its own unit (property) id. Reuses the same
// tenant extraction as usePropertyRentalContracts.
// The leases of these units, and whether they have arrived: a list that is
// still empty because it is fetching is no units without leases.
export function useGroupRentalContractsState(unitIds: number[]): {
  rentals: RentalContract[];
  isLoaded: boolean;
} {
  const enabled = unitIds.length > 0;
  const ids = enabled ? unitIds : [-1];
  const key = ids.join(",");

  // Multi-property fetch: OR-of-EQs mirrors the pattern that works for
  // property costs (`useInvoices` above). `propertyId: { inq: [...] }`
  // returned zero contracts even for panden that clearly have them —
  // presumably the contract endpoint's per-property authorization drops the
  // `inq` shape. Also: no stopDate clause so open-ended contracts (stopDate
  // null) are included, mirroring `usePropertyRentalContracts`.
  const propertyOr =
    ids.length === 1
      ? { propertyId: { eq: ids[0] } }
      : { or: ids.map(id => ({ propertyId: { eq: id } })) };
  const { items, isFetching } = contractHooksV2.useGetAll({
    shouldRefetch: enabled,
    query: {
      filter: {
        and: [{ contractType: { eq: EContractType.Basic } }, propertyOr],
      },
      sort: CONTRACTS_PAGING_SORT,
    },
  });

  const rentPayerByContract = useRentPayers(enabled ? unitIds : []);

  const rentals = useMemo(() => {
    if (!enabled) return [];
    return uniqueById(items).map((c: any) =>
      mapContractToRental({
        c,
        unitId: String(c.propertyId),
        rentPayerId: rentPayerByContract[String(c.id)],
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, items, key, rentPayerByContract]);

  return { rentals, isLoaded: enabled && items !== undefined && !isFetching };
}

// The same leases, without their loading state.
export function useGroupRentalContracts(unitIds: number[]): RentalContract[] {
  return useGroupRentalContractsState(unitIds).rentals;
}

// The units of a group = the real properties whose parentId is this group,
// for the apportionment-key editor. Archived units are listed (marked) too:
// their weight still splits the periods in which they existed, and saving the
// key must not drop it.
export function useUnits(groupId: string) {
  const { units, isFetching } = useGroupUnits(groupId);

  const items = useMemo(
    () =>
      units.map(unit => ({
        id: String(unit.id),
        code: unit.label,
        address: unitDisplayLabel({
          label: unit.address,
          archivedOn: unit.archivedOn,
        }),
        type: "unit" as const,
      })),
    [units],
  );

  return { items, isFetching };
}

// Real broker properties (panden/groepen) — used to assign a cost and to create
// the real payment order against an actual property.
export function useAssignableProperties() {
  // useGetAll pages through the office's full portfolio — a single page of 100
  // hid units of groups (and cost-assignment targets) for larger offices.
  const { items, isFetching } = propertyHooks.useGetAll({
    shouldRefetch: true,
    query: {
      filter: { archivedAt: { is: null } },
    },
  });

  const mapped = (items ?? []).map((p: any) => ({
    id: String(p.id),
    code: p.name ?? formatAddress(p, false),
    // Full address: "Straat nummer bus, postcode stad" (showZip on).
    address: formatAddress(p),
    type:
      p.typeId === EPropertyTypeId.Group
        ? ("building" as const)
        : ("unit" as const),
    parentId: p.parentId != null ? String(p.parentId) : undefined,
    box: typeof p.box === "string" && p.box.trim() ? p.box.trim() : undefined,
    addressParts: addressPartsOf(p),
    // The property's office: the sender of its settlement statements.
    ...(p.brokerId != null ? { brokerId: Number(p.brokerId) } : {}),
  }));

  return { items: mapped, isFetching };
}

// ----- Invoices -----

// The cost module's list actions: its own actions module does not export
// them, and these are the same actions (an action is its type) the list hook
// dispatches and its saga answers.
const propertyCostListActions = generateActions<IPropertyCost, string>(
  PROPERTY_COST_GENERATOR_PARAMS,
);

// Fetches every cost of `query` now and resolves with them once all pages are
// in; the store gets them too, so the list hook on the same query follows.
// The unthrottled action: it neither waits out a fetch already running nor
// is skipped as a repeat within the throttle window, so the caller sees the
// costs as they are now.
export const fetchCostsNow = (params: {
  dispatch: (action: { type: string; payload: unknown }) => unknown;
  query: IGetAllQueryAsAction;
}): Promise<Invoice[]> => {
  const { dispatch, query } = params;
  return new Promise<Invoice[]>((resolve, reject) => {
    dispatch(
      propertyCostListActions.getAllWhereStartNoThrottle.getAction({
        query,
        onSuccess: result =>
          resolve(
            uniqueById(result as IPropertyCost[]).map(propertyCostToInvoice),
          ),
        onFailure: reject,
      }),
    );
  });
};

// Cost list, backed by the real `/property-costs` endpoint. A cost is scoped to
// one property; the scene passes the property id as groupId (building) or
// unitId (unit). When neither is given the global costs page fetches every
// cost the user has access to (backend permission-scoped). When `parentGroupId`
// is provided (a unit inside a group), costs of the parent group are also
// listed so users see "their" share of group-level costs on the unit page.
export function useInvoices(filter: {
  groupId?: string;
  unitId?: string;
  parentGroupId?: string;
  unitIds?: string[];
}) {
  const dispatch = useDispatch();
  const primaryId = Number(filter.unitId ?? filter.groupId);
  const parentId = filter.parentGroupId
    ? Number(filter.parentGroupId)
    : Number.NaN;
  const extraIds = (filter.unitIds ?? [])
    .map(Number)
    .filter(n => !Number.isNaN(n));
  const ids = [...new Set([primaryId, parentId, ...extraIds])].filter(
    n => !Number.isNaN(n),
  );

  const query = useMemo(() => {
    let filterClause: Record<string, unknown> = {};
    if (ids.length === 1) {
      filterClause = { propertyId: { eq: ids[0] } };
    } else if (ids.length > 1) {
      filterClause = { or: ids.map(id => ({ propertyId: { eq: id } })) };
    }
    return { filter: filterClause };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(",")]);

  // useGetAll pages through EVERY cost — the settlement wizard and the engine
  // need the complete list; a single page silently dropped costs beyond the
  // default fetch limit.
  const {
    items: rawItems,
    isFetching,
    fetchError,
    refetch,
  } = propertyCostHooks.useGetAll({ shouldRefetch: true, query });

  const items = useMemo(
    () =>
      uniqueById(rawItems as IPropertyCost[] | undefined).map(
        propertyCostToInvoice,
      ),
    [rawItems],
  );

  // `usePaged` only dispatches its fetch from an effect, so on the first render
  // isFetching is still false while items is undefined. Callers that keyed
  // their empty state on isFetching alone therefore flashed "Nog geen kosten"
  // for a beat. Undefined items = never loaded, which is the reliable signal.
  const isLoading = isFetching || rawItems === undefined;
  // A failed fetch must never read as "no costs": a settlement then went
  // through without them. Callers say so and offer a retry (refetch).
  const loadFailed = hasLoadFailed({ isFetching, fetchError });

  // The costs as they are now (fetchCostsNow). Unlike `refetch`, which does
  // nothing while a fetch runs and is throttled, for a caller that must see
  // the costs as they are (after the server refused a confirm on stale ones).
  const reload = useCallback(
    () => fetchCostsNow({ dispatch, query }),
    [dispatch, query],
  );

  const create = useCallback(
    async (payload: Omit<Invoice, "id">) => {
      const created = await propertyCostPromises.create(dispatch, {
        data: invoiceToCreateDto(payload, filter),
      });
      return propertyCostToInvoice(unwrapEntity<IPropertyCost>(created));
    },
    [dispatch, filter],
  );

  const update = useCallback(
    async (id: string, patch: Partial<Invoice>) => {
      const updated = await propertyCostPromises.update(dispatch, {
        id,
        data: invoicePatchToUpdateDto(patch),
      });
      if (patch.supplier !== undefined || patch.description !== undefined) {
        const orderIds = [
          ...new Set(
            (patch.paymentSplits ?? [])
              .map(s => s.paymentOrderId)
              .filter((v): v is string => !!v)
              .map(v => Number(v))
              .filter(n => !Number.isNaN(n)),
          ),
        ];
        if (orderIds.length > 0) {
          const otherDescription = [patch.supplier, patch.description]
            .filter(Boolean)
            .join(" — ");
          await Promise.all(
            orderIds.map(ordId =>
              paymentOrderPromises.update(dispatch, {
                id: ordId,
                data: { otherDescription, updatePaymentRequests: true },
              }),
            ),
          );
        }
      }
      return propertyCostToInvoice(unwrapEntity<IPropertyCost>(updated));
    },
    [dispatch],
  );

  // Multi-payer splits are owned wholesale by the cost: apply the patch to the
  // matching split and PATCH the entire array.
  const updateSplit = useCallback(
    async (
      invoiceId: string,
      splitId: string,
      patch: Partial<Omit<PaymentSplit, "id">>,
    ) => {
      const current = items.find(i => i.id === invoiceId);
      const nextSplits = (current?.paymentSplits ?? []).map(s =>
        s.id === splitId ? { ...s, ...patch } : s,
      );
      const updated = await propertyCostPromises.update(dispatch, {
        id: invoiceId,
        data: { paymentSplits: nextSplits },
      });
      return propertyCostToInvoice(unwrapEntity<IPropertyCost>(updated));
    },
    [dispatch, items],
  );

  const remove = useCallback(
    async (id: string) => {
      // The caller explains a refusal in a dialog; the generic toast would
      // show the server's English text next to it.
      await propertyCostPromises.remove(dispatch, {
        id,
        suppressFailureAlert: true,
      });
    },
    [dispatch],
  );

  return {
    items,
    isFetching,
    isLoading,
    loadFailed,
    refetch,
    reload,
    create,
    update,
    updateSplit,
    remove,
  };
}

// Fetch all children of a single group-cost by their shared `groupCostRef`.
// Used by the drawer + group-page aggregation to reconstruct the group total,
// the per-unit split, and the "onderdeel van groepskost" banner from any one
// child cost. Returns an empty list when no ref is given (skips the fetch).
export function useSiblingInvoices(groupCostRef?: string) {
  const query = useMemo(
    () =>
      groupCostRef
        ? { filter: { groupCostRef: { eq: groupCostRef } } }
        : undefined,
    [groupCostRef],
  );
  const { items: rawItems, isFetching } = propertyCostHooks.useGetAll({
    shouldRefetch: !!groupCostRef,
    query,
  });
  const items = useMemo(
    () =>
      groupCostRef
        ? uniqueById(rawItems as IPropertyCost[] | undefined)
            .map(propertyCostToInvoice)
            // Client-side safety net in case the backend ignores the filter —
            // only keep costs that truly share the ref.
            .filter(i => i.groupCostRef === groupCostRef)
        : [],
    [rawItems, groupCostRef],
  );
  return { items, isFetching };
}

// The costs on a unit's parent group, for the unit page: a group cost asked
// straight from the tenants bills this unit's tenants without being on the
// unit's own ledger. No fetch without a parent group.
export function useParentGroupInvoices(parentGroupId?: string): Invoice[] {
  const groupId = Number(parentGroupId);
  const enabled = !!parentGroupId && !Number.isNaN(groupId);
  // The same query as useInvoices({ groupId }), so both share one list.
  const query = useMemo(
    () => (enabled ? { filter: { propertyId: { eq: groupId } } } : undefined),
    [enabled, groupId],
  );
  const { items: rawItems } = propertyCostHooks.useGetAll({
    shouldRefetch: enabled,
    query,
  });
  return useMemo(
    () =>
      enabled
        ? uniqueById(rawItems as IPropertyCost[] | undefined).map(
            propertyCostToInvoice,
          )
        : [],
    [enabled, rawItems],
  );
}

// INFO: the payment state of a cost follows its payments on the server: the
// payment -> cost syncs in rentio-api-next (paid by hand, request or order
// withdrawn) and rentio-api-serverless (paid online) update its splits as a
// system change. The dashboard only reads it (useCostPaymentStatusMaps), so
// opening a cost list never writes a change in the viewer's name.

// ----- Keys -----

export function useKeys(groupId: string) {
  const dispatch = useDispatch();
  const propertyId = Number(groupId);
  const enabled = !!groupId && Number.isFinite(propertyId);

  const {
    items: dtos,
    isFetching,
    refetch,
  } = apportionmentKeyHooks.useGetAll({
    shouldRefetch: enabled,
    query: enabled ? { filter: { propertyId: { eq: propertyId } } } : undefined,
  });

  // Real (persisted) custom keys, plus a synthesized built-in "Gelijke
  // verdeling" when the group has none yet. The system key is not persisted —
  // it mirrors the mock's auto-created equal-split key and keeps the wizard's
  // default working without writing a row on read.
  const items: ApportionmentKey[] = useMemo(() => {
    const real = uniqueById(dtos as IApportionmentKey[] | undefined).map(
      keyFromDto,
    );
    if (real.some(k => k.isSystem)) return real;
    const systemKey: ApportionmentKey = {
      id: `system-${groupId}`,
      groupId,
      name: getLocalizedText("costs.keys.equal_split"),
      isSystem: true,
      isEqual: true,
      weights: {},
      createdAt: "",
      updatedAt: "",
    };
    return [systemKey, ...real];
  }, [dtos, groupId]);

  const create = useCallback(
    async (
      payload: Omit<
        ApportionmentKey,
        "id" | "createdAt" | "updatedAt" | "isSystem"
      >,
    ) => {
      const dto = await apportionmentKeyPromises.create(dispatch, {
        data: keyToCreateDto(payload),
      });
      refetch();
      return keyFromDto(unwrapEntity<IApportionmentKey>(dto));
    },
    [dispatch, refetch],
  );

  const update = useCallback(
    async (id: string, patch: Partial<ApportionmentKey>) => {
      const dto = await apportionmentKeyPromises.update(dispatch, {
        id,
        data: keyPatchToUpdateDto(patch),
      });
      refetch();
      return keyFromDto(unwrapEntity<IApportionmentKey>(dto));
    },
    [dispatch, refetch],
  );

  const remove = useCallback(
    async (id: string) => {
      await apportionmentKeyPromises.remove(dispatch, { id });
      refetch();
    },
    [dispatch, refetch],
  );

  return { items, isFetching, refetch, create, update, remove };
}

// ----- Settlements -----

// Without the unwrap, create() returned an id-less object and the wizard
// navigated to /settlements/undefined.
const unwrapSettlement = (dto: unknown): ISettlement =>
  unwrapEntity<ISettlement>(dto);

export function useSettlements(filter: {
  groupId?: string;
  unitId?: string;
  // Group scope: the group's child units. A cost of a unit can be settled from
  // a settlement on that unit, so the group must see those settlements too —
  // otherwise its own list shows such a cost as still "to settle" and its
  // wizard offers it again, settling it twice.
  unitIds?: string[];
}) {
  const dispatch = useDispatch();
  const primaryId = Number(filter.groupId ?? filter.unitId);
  const extraIds = (filter.unitIds ?? [])
    .map(Number)
    .filter(n => !Number.isNaN(n));
  const ids = [...new Set([primaryId, ...extraIds])].filter(
    n => !Number.isNaN(n),
  );

  // Complete list: the double-settlement guard derives the already-settled
  // cost ids from EVERY confirmed settlement, so this may never be capped.
  const query = useMemo(() => {
    if (ids.length === 0) return {};
    if (ids.length === 1) return { filter: { propertyId: { eq: ids[0] } } };
    return { filter: { or: ids.map(id => ({ propertyId: { eq: id } })) } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(",")]);

  const { items, isFetching, fetchError, refetch } = settlementHooks.useGetAll({
    shouldRefetch: true,
    query,
  });

  const mapped = useMemo(
    () => uniqueById(items as ISettlement[] | undefined).map(settlementFromApi),
    [items],
  );

  // See useInvoices: undefined items = the effect hasn't fetched yet, so the
  // list must read as loading instead of empty.
  const isLoading = isFetching || items === undefined;
  // See useInvoices: without the settlements the double-settlement guard
  // sees no settled cost.
  const loadFailed = hasLoadFailed({ isFetching, fetchError });

  const create = useCallback(
    async (payload: Omit<Settlement, "id" | "createdAt">) => {
      // The wizard explains a refusal in its own dialog: the generic toast
      // repeated the server's English message, settlement id included.
      const dto = await settlementPromises.create(dispatch, {
        data: settlementToCreateDto(payload),
        suppressSuccessAlert: true,
        suppressFailureAlert: true,
      });
      refetch();
      return settlementFromApi(unwrapSettlement(dto));
    },
    [dispatch, refetch],
  );

  const remove = useCallback(
    async (settlementId: string) => {
      // The caller explains a refusal in a dialog (see useInvoices.remove).
      await settlementPromises.remove(dispatch, {
        id: settlementId,
        suppressFailureAlert: true,
      });
      refetch();
    },
    [dispatch, refetch],
  );

  // Patches a settlement and refreshes the list. No failure toast: the caller
  // (the wizard linking its payment orders) says itself what went wrong.
  const update = useCallback(
    async (settlementId: string, patch: Partial<Settlement>) => {
      await settlementPromises.update(dispatch, {
        id: settlementId,
        data: settlementPatchToUpdateDto(patch),
        suppressSuccessAlert: true,
        suppressFailureAlert: true,
      });
      refetch();
    },
    [dispatch, refetch],
  );

  // Records payment links on a confirmed settlement: only the actions given,
  // the server keeps every other one as stored (N08), and the frozen
  // statements are not sent again (decision 16 of 2026-10-20).
  const recordActions = useCallback(
    async (settlementId: string, actions: SettlementAction[]) => {
      await settlementPromises.update(dispatch, {
        id: settlementId,
        data: { snapshot: { actions } },
        suppressSuccessAlert: true,
        suppressFailureAlert: true,
      });
      refetch();
    },
    [dispatch, refetch],
  );

  return {
    items: mapped,
    isFetching,
    isLoading,
    loadFailed,
    refetch,
    create,
    remove,
    update,
    recordActions,
  };
}

export function useSettlement(id: string | undefined) {
  const dispatch = useDispatch();
  const { detail, isFetching, fetchError, refetch } = settlementHooks.useDetail(
    {
      id,
      shouldRefetch: !!id,
    },
  );

  const settlement = useMemo(
    () => (detail ? settlementFromApi(detail as ISettlement) : null),
    [detail],
  );

  const updateDraft = useCallback(
    async (
      patch: Partial<Settlement>,
      options?: {
        // The caller explains a refusal in a dialog of its own (the
        // confirmed view): the generic toast repeated the server's English
        // message next to it.
        suppressFailureAlert?: boolean;
      },
    ) => {
      if (!id) return;
      // Draft autosaves on every wizard interaction: no success toast, and no
      // refetch (the wizard's local draft is the source of truth while editing —
      // refetching would flash a reload on each step).
      const dto = await settlementPromises.update(dispatch, {
        id,
        data: settlementPatchToUpdateDto(patch),
        suppressSuccessAlert: true,
        suppressFailureAlert: !!options?.suppressFailureAlert,
      });
      return settlementFromApi(unwrapSettlement(dto));
    },
    [dispatch, id],
  );

  // Records payment links and tenant marks on a confirmed settlement: only
  // the actions given, the server keeps every other one as stored (N08), and
  // the rest of the snapshot (the frozen statements) is not sent again.
  const recordActions = useCallback(
    async (actions: SettlementAction[]) => {
      if (!id) return;
      await settlementPromises.update(dispatch, {
        id,
        data: { snapshot: { actions } },
        suppressSuccessAlert: true,
        suppressFailureAlert: true,
      });
    },
    [dispatch, id],
  );

  // The settlement as stored right now, read again. The store follows, so
  // every view of it shows the same. Before an invitation is sent: another
  // tab may have sent one since this view loaded (see inviteGuard.utils).
  const reload = useCallback(async () => {
    if (!id) return undefined;
    const dto = await settlementPromises.getDetail(dispatch, { id });
    return settlementFromApi(unwrapSettlement(dto));
  }, [dispatch, id]);

  const confirm = useCallback(
    async (snapshot: SettlementSnapshot) => {
      if (!id) return;
      // Freeze: status -> confirmed, store the snapshot, clear the draft.
      const dto = await settlementPromises.update(dispatch, {
        id,
        data: settlementPatchToUpdateDto({
          status: "confirmed",
          confirmedAt: todayIsoDay(),
          draftState: undefined,
          snapshot,
        }),
        suppressSuccessAlert: true,
        // Refused by the double-settlement guard like a create: the wizard's
        // own dialog explains it.
        suppressFailureAlert: true,
      });
      refetch();
      return settlementFromApi(unwrapSettlement(dto));
    },
    [dispatch, id, refetch],
  );

  const remove = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!id) return;
      await settlementPromises.remove(dispatch, {
        id,
        suppressSuccessAlert: !!opts?.silent,
      });
    },
    [dispatch, id],
  );

  // fetchError tells a settlement that is gone or not the viewer's (404/403)
  // from one still loading (see getMissingDetailState).
  return {
    settlement,
    isFetching,
    fetchError,
    refetch,
    reload,
    updateDraft,
    recordActions,
    confirm,
    remove,
  };
}

// ----- Live payment status (shared by settlements + costs) -----

export interface OrderPaymentStatus {
  status: EPaymentRequestStatus;
  // Both dates are Brussels calendar days, "YYYY-MM-DD" (toBrusselsIsoDay).
  paidAt?: string;
  // When the payment request was created — shown as the "verzonden" date.
  createdAt?: string;
}

export interface PaymentStatusMaps {
  byOrderId: Record<number, OrderPaymentStatus>;
  // Bulk creation puts several tenants' requests under ONE order; the request
  // id is the only key that tells them apart, so per-tenant status reads
  // prefer this map when the settlement action pinned a paymentRequestId.
  byRequestId: Record<number, OrderPaymentStatus>;
  // Whether the payment requests behind these maps have loaded.
  requestsLoaded?: boolean;
  // The linked orders that ended (deleted or completed), see
  // withoutWithdrawnLink. Undefined while unknown: nothing is withdrawn then.
  endedOrderIds?: ReadonlySet<number>;
}

// The payment-request list's default sort (createdAt) is not unique either:
// bulk invitations share their second, so paging could skip a request and
// its cost or settlement kept "invitation sent" after it was paid.
export const PAYMENT_REQUESTS_PAGING_SORT: TSort = [
  { field: "createdAt", method: "ASC" },
  { field: "id", method: "ASC" },
];

// Live status + paid date per payment-order AND per payment-request id,
// derived from the v2 payment requests of the given properties. Shared by
// settlements AND costs so both reflect the real payment state (online +
// manual) instead of a stored copy.
function usePaymentStatusMaps(
  linkedOrderIds: number[],
  propertyIds: number[],
): PaymentStatusMaps {
  const filter =
    propertyIds.length === 1
      ? { requestPropertyId: { eq: propertyIds[0] } }
      : propertyIds.length > 1
        ? { or: propertyIds.map(id => ({ requestPropertyId: { eq: id } })) }
        : { requestPropertyId: { eq: -1 } };

  const { items } = paymentRequestHooks.useGetAll({
    shouldRefetch: linkedOrderIds.length > 0 && propertyIds.length > 0,
    query: { filter, sort: PAYMENT_REQUESTS_PAGING_SORT },
  });

  return useMemo(() => {
    const byOrderId: Record<number, OrderPaymentStatus> = {};
    const byRequestId: Record<number, OrderPaymentStatus> = {};
    for (const r of items ?? []) {
      // The owner's payout under the tenant's request has its own timing (a
      // planned payout stays new after the tenant paid): only the tenant's
      // own request says whether they paid, as on the server.
      if (r.payoutType === EPayoutType.Payout) continue;
      const entry: OrderPaymentStatus = {
        status: r.status,
        paidAt: toBrusselsIsoDay((r as any).paidAt),
        createdAt: toBrusselsIsoDay((r as any).createdAt),
      };
      const isPaid = isTenantPaidStatus(r.status);
      if (r.id != null) byRequestId[r.id] = entry;
      for (const item of (r as any).paymentRequestItems ?? []) {
        const oid = item.paymentOrder?.id;
        if (oid == null) continue;
        if (byOrderId[oid] == null || isPaid) {
          byOrderId[oid] = entry;
        }
      }
    }
    return { byOrderId, byRequestId, requestsLoaded: items !== undefined };
  }, [items]);
}

export function useSettlementPaymentStatus(
  settlements: Settlement[],
  propertyIds: number[],
): PaymentStatusMaps {
  const linkedOrderIds = useMemo(() => {
    const ids: number[] = [];
    for (const s of settlements) {
      for (const a of s.snapshot?.actions ?? []) {
        if (a.paymentOrderId != null) ids.push(a.paymentOrderId);
      }
    }
    return ids;
  }, [settlements]);

  const statusMaps = usePaymentStatusMaps(linkedOrderIds, propertyIds);
  // An invitation withdrawn under Payments no longer counts as sent or open.
  const endedOrderIds = useEndedPaymentOrderIds({
    linkedOrderIds,
    requestsLoaded: !!statusMaps.requestsLoaded,
  });
  return useMemo(
    () => ({ ...statusMaps, endedOrderIds }),
    [statusMaps, endedOrderIds],
  );
}

// Same live derivation for costs: gate on the payment-order ids linked to the
// costs' payment splits, so a cost's Betaalstatus follows the real payment
// (paid online or marked paid) exactly like a settlement does.
export function useCostPaymentStatusMaps(
  invoices: Invoice[],
  propertyIds: number[],
): PaymentStatusMaps {
  const linkedOrderIds = useMemo(() => {
    const ids: number[] = [];
    for (const inv of invoices) {
      for (const s of inv.paymentSplits ?? []) {
        if (s.paymentOrderId != null) ids.push(Number(s.paymentOrderId));
      }
    }
    return ids;
  }, [invoices]);

  return usePaymentStatusMaps(linkedOrderIds, propertyIds);
}

export type SettlementPayStatusVM = TSettlementPayStatus;

// A settlement's payment orders and requests are tenant-facing, exactly like
// a cost's betaaluitnodiging. Deleting the settlement is blocked while any of
// them is still OPEN — the broker withdraws that payment under Betalingen
// first, a conscious act, never a side effect of a delete. Paid ones don't
// block: nothing is open to withdraw and the payment history stays. Unknown
// status (maps still loading) counts as open — the safe default; a payment
// withdrawn under Payments is no payment any more (see withoutWithdrawnLink).
export function settlementHasOpenPayment(
  s: Settlement,
  statusMaps: PaymentStatusMaps,
): boolean {
  // A manual "paid" mark does not close a linked request (see
  // isSettlementActionOpen).
  return settlementOpenPayments({ settlement: s, statusMaps }).length > 0;
}

// The tenants' payment status of one settlement, for the lists: how far all
// its tenants are, who pay and who get money back (settlementPayStatusOf),
// each paid or not by the drawer's own rule
// (isSettlementActionPaid on the live request), each refund done once it is
// marked (isSettlementRefundDone).
export function settlementTenantPaymentStatus(
  s: Settlement,
  statusMaps: PaymentStatusMaps,
): SettlementPayStatusVM {
  const statusFor = (a: SettlementAction): OrderPaymentStatus | undefined =>
    getActionPaymentStatus({ action: a, statusMaps });
  return settlementPayStatusOf({
    settlement: s,
    actions: getLiveSettlementActions({
      actions: s.snapshot?.actions ?? [],
      statusMaps,
    }),
    // Paid = the real payment says so, or — only when no payment request is
    // linked — the broker manually marked THIS tenant as paid in the
    // confirmed view (see isSettlementActionPaid).
    isPaid: a =>
      isSettlementActionPaid({
        action: a,
        requestStatus: statusFor(a)?.status,
      }),
    paidAt: a =>
      getSettlementActionPaidAt({
        action: a,
        requestPaidAt: statusFor(a)?.paidAt,
      }),
    isRefunded: isSettlementRefundDone,
    refundedAt: getSettlementRefundDoneAt,
  });
}

// Whether the API lets the signed-in user change the costs, keys and
// settlements of one property (GET /settlements/edit-access): the owner of a
// group record in a building with other owners only reads their own part.
// `null` while it loads or without a property; false when the request fails.
const fetchCostsEditAccess = (propertyId: string | number): Promise<boolean> =>
  api
    .get<{ data: { canEdit: boolean } }>(
      `/settlements/edit-access?propertyId=${encodeURIComponent(
        String(propertyId),
      )}`,
      { isV2: true },
    )
    .then(response => response.data?.data?.canEdit === true)
    .catch(() => false);

const useCostsEditAccess = (
  propertyId: string | number | undefined,
): boolean | null => {
  const [canEdit, setCanEdit] = useState<boolean | null>(null);

  useEffect(() => {
    if (propertyId == null || propertyId === "") {
      setCanEdit(null);
      return;
    }
    let cancelled = false;
    setCanEdit(null);
    fetchCostsEditAccess(propertyId).then(answer => {
      if (!cancelled) setCanEdit(answer);
    });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  return canEdit;
};

// Whether the signed-in user may add, change or delete costs, keys and
// settlements, and whether that answer is still loading
// (resolveCostsEditPermission). A page that acts on a refusal, like sending
// the user away, waits for `isLoading` to clear.
export const useCostsEditPermission = (
  propertyId?: string | number,
): ICostsEditPermission => {
  const { user, isBroker } = authHooks.useSelf();
  const hasCreateAccess = !!user?.hasCreateAccess;
  const propertyAccess = useCostsEditAccess(
    needsCostsEditCheck({ isBroker, hasCreateAccess, propertyId })
      ? propertyId
      : undefined,
  );

  return resolveCostsEditPermission({
    isBroker,
    hasCreateAccess,
    propertyId,
    propertyAccess,
  });
};

// useCostsEditPermission for a page that only shows or hides its write
// buttons: false while the answer loads.
export const useCanEditCosts = (propertyId?: string | number): boolean =>
  useCostsEditPermission(propertyId).canEdit;

// useCanEditCosts for several properties at once, e.g. the units of a
// building whose own costs the building page lists (Q7 of 2026-10-16): the
// costs of a unit are changed there when the user may change that unit's.
// `canEditOf` is false for a property not asked about, and while the answer
// loads (`isLoading`).
export const useCanEditCostsOf = (
  propertyIds: string[],
): { canEditOf: (propertyId?: string) => boolean; isLoading: boolean } => {
  const { user, isBroker } = authHooks.useSelf();
  const hasCreateAccess = !!user?.hasCreateAccess;
  const idsKey = uniq(propertyIds).sort().join(",");
  const askedIds = useMemo(
    () =>
      idsKey
        .split(",")
        .filter(propertyId =>
          needsCostsEditCheck({ isBroker, hasCreateAccess, propertyId }),
        ),
    [idsKey, isBroker, hasCreateAccess],
  );
  const [access, setAccess] = useState<Record<string, boolean> | null>(null);

  useEffect(() => {
    if (askedIds.length === 0) {
      setAccess({});
      return;
    }
    let cancelled = false;
    setAccess(null);
    Promise.all(
      askedIds.map(propertyId =>
        fetchCostsEditAccess(propertyId).then(
          answer => [propertyId, answer] as const,
        ),
      ),
    ).then(answers => {
      if (!cancelled) setAccess(Object.fromEntries(answers));
    });
    return () => {
      cancelled = true;
    };
  }, [askedIds]);

  const canEditOf = useCallback(
    (propertyId?: string) => {
      if (!propertyId || !idsKey.split(",").includes(propertyId)) return false;
      return resolveCostsEditPermission({
        isBroker,
        hasCreateAccess,
        propertyId,
        propertyAccess: access ? access[propertyId] ?? false : null,
      }).canEdit;
    },
    [idsKey, isBroker, hasCreateAccess, access],
  );

  return { canEditOf, isLoading: askedIds.length > 0 && access === null };
};
